import { useContext, useState } from "react";
import { useNavigate } from "react-router";

import { ApiContext } from "../../api-context";

import type {
  AddPrefixRequestBody,
  GetPrefixRequestBody,
  GetPrefixSuccessResponse,
} from "@sperrdatei/dto/types";

export type AddPrefixPageProps = object;

export const AddPrefixPage: React.FC<AddPrefixPageProps> = () => {
  const { apiFetch } = useContext(ApiContext);
  const navigate = useNavigate();

  const [prefix, setPrefix] = useState("");
  const [result, setResult] = useState("");
  const [prefixError, setPrefixError] = useState<string | undefined>(undefined);

  const validatePrefix = async (): Promise<boolean> => {
    if (prefix === "") {
      setPrefixError("Angabe notwendig");
      return false;
    }

    if (prefix.length < 3) {
      setPrefixError("Die Mindestlänge ist 3");
      return false;
    }

    if (!prefix.startsWith("0")) {
      setPrefixError("Die Vorwahl muss mit einer '0' beginnen");
      return false;
    }

    const body: GetPrefixRequestBody = { number: prefix };
    const response = await apiFetch("/api/number/prefix", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (response.ok) {
      const result = (await response.json()) as GetPrefixSuccessResponse;
      setPrefixError(
        `Die Vorwahl ${result.prefix} ist bereits in der Datenbank`,
      );
      return false;
    }

    if (response.status === 404) {
      return true;
    }

    navigate("/error");
    return false;
  };

  const addPrefix = async (): Promise<void> => {
    const isValidPrefix = await validatePrefix();
    if (!isValidPrefix) {
      return;
    }

    const body: AddPrefixRequestBody = { prefix };
    const response = await apiFetch("/api/admin/prefix/add", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (response.ok) {
      setResult(prefix);
      setPrefix("");
      return;
    }

    navigate("/error");
  };

  return (
    <div className="container ms-0">
      <div className="col-xxl-4 col-xl-5 col-lg-7 col-xs-9 mt-0 p-0">
        {result ? (
          <>
            <h6>Vorwahl hinzugefügt</h6>
            <p>
              Die Vorwahl <strong>{result}</strong> wurde in die Datenbank
              eingetragen und kann jetzt verwendet werden.
            </p>
            <button
              type="button"
              className="btn btn-danger mt-2"
              onClick={() => setResult("")}
            >
              Zurück
            </button>
          </>
        ) : (
          <>
            <h3 className="page-caption mb-3">Vorwahl hinzufügen</h3>
            <p>Hier können Sie eine neue Vorwahl hinzufügen</p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                addPrefix();
              }}
              className="container p-0"
              noValidate
            >
              <label htmlFor="number" className="form-text">
                <strong>Vorwahl</strong>
              </label>
              <input
                type="text"
                id="number"
                value={prefix}
                maxLength={6}
                className={
                  prefixError ? "form-control is-invalid" : "form-control "
                }
                onChange={(e) => setPrefix(e.target.value)}
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /[^0-9]/g,
                    "",
                  );
                }}
                required
              />
              <div id="prefixFeedback" className="invalid-feedback">
                {prefixError}
              </div>
              <button
                type="button"
                className="btn btn-danger mt-2"
                onClick={() => addPrefix()}
              >
                Speichern
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
